import { useEffect, useState } from "react";
import api, { API, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Download } from "lucide-react";

export default function AdminContracts() {
  const [contracts, setContracts] = useState([]);
  const [machinery, setMachinery] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/contracts").then((r) => setContracts(r.data));
  useEffect(() => { load(); api.get("/machinery").then((r) => setMachinery(r.data)); }, []);

  const openNew = () => {
    setForm({
      machinery_id: "", type: "alquiler", client_name: "", client_email: "",
      client_id_number: "", client_address: "", start_date: "", end_date: "",
      amount: "", currency: "USD", terms: "", status: "activo",
    });
    setOpen(true);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/contracts", { ...form, amount: parseFloat(form.amount) });
      toast.success("Contrato creado");
      setOpen(false);
      load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display font-black text-3xl tracking-tight">CONTRATOS</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button onClick={openNew} data-testid="add-contract-btn" className="flex items-center gap-2 px-5 py-3 bg-brand text-black font-semibold hover:bg-brand-hover transition-colors"><Plus size={18} /> Nuevo contrato</button>
          </DialogTrigger>
          {form && (
            <DialogContent className="sm:max-w-lg">
              <DialogHeader><DialogTitle className="font-display text-2xl">Nuevo contrato</DialogTitle></DialogHeader>
              <form onSubmit={save} className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
                <div><Label>Maquinaria *</Label>
                  <select required value={form.machinery_id} onChange={set("machinery_id")} data-testid="contract-machinery" className="w-full h-10 px-2 bg-background border border-input text-sm">
                    <option value="">Seleccionar...</option>
                    {machinery.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Tipo</Label>
                    <select value={form.type} onChange={set("type")} className="w-full h-10 px-2 bg-background border border-input text-sm">
                      <option value="alquiler">Alquiler</option><option value="venta">Venta</option>
                    </select>
                  </div>
                  <div><Label>Estado</Label>
                    <select value={form.status} onChange={set("status")} className="w-full h-10 px-2 bg-background border border-input text-sm">
                      <option value="activo">Activo</option><option value="borrador">Borrador</option><option value="finalizado">Finalizado</option><option value="cancelado">Cancelado</option>
                    </select>
                  </div>
                </div>
                <div><Label>Cliente *</Label><Input required value={form.client_name} onChange={set("client_name")} data-testid="contract-client" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Correo *</Label><Input type="email" required value={form.client_email} onChange={set("client_email")} data-testid="contract-email" /></div>
                  <div><Label>Doc / RUC</Label><Input value={form.client_id_number} onChange={set("client_id_number")} /></div>
                </div>
                <div><Label>Dirección</Label><Input value={form.client_address} onChange={set("client_address")} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Fecha inicio</Label><Input type="date" value={form.start_date} onChange={set("start_date")} /></div>
                  <div><Label>Fecha fin</Label><Input type="date" value={form.end_date} onChange={set("end_date")} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Monto *</Label><Input type="number" required value={form.amount} onChange={set("amount")} data-testid="contract-amount" /></div>
                  <div><Label>Moneda</Label><Input value={form.currency} onChange={set("currency")} /></div>
                </div>
                <div><Label>Términos y condiciones</Label><Textarea rows={3} value={form.terms} onChange={set("terms")} placeholder="Dejar vacío para usar términos estándar" /></div>
                <button type="submit" disabled={saving} data-testid="contract-save" className="w-full h-11 bg-brand text-black font-semibold hover:bg-brand-hover transition-colors">{saving ? "Creando..." : "Crear contrato"}</button>
              </form>
            </DialogContent>
          )}
        </Dialog>
      </div>

      {contracts.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground">Aún no hay contratos.</div>
      ) : (
        <div className="border border-border overflow-x-auto" data-testid="admin-contracts-table">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-left"><tr>
              <th className="p-3 font-semibold">N°</th><th className="p-3 font-semibold">Cliente</th><th className="p-3 font-semibold">Equipo</th><th className="p-3 font-semibold">Tipo</th><th className="p-3 font-semibold">Monto</th><th className="p-3 font-semibold">Estado</th><th className="p-3"></th>
            </tr></thead>
            <tbody>
              {contracts.map((c) => (
                <tr key={c.id} className="border-t border-border">
                  <td className="p-3 font-mono text-xs">{c.contract_number}</td>
                  <td className="p-3">{c.client_name}<div className="text-xs text-muted-foreground">{c.client_email}</div></td>
                  <td className="p-3">{c.machinery_name}</td>
                  <td className="p-3 capitalize">{c.type}</td>
                  <td className="p-3">{c.currency} {c.amount?.toLocaleString()}</td>
                  <td className="p-3 capitalize">{c.status}</td>
                  <td className="p-3">
                    <a href={`${API}/contracts/${c.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`contract-pdf-${c.id}`}
                      className="flex items-center gap-1 text-brand font-semibold hover:underline"><Download size={14} /> PDF</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
